import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Spin, Collapse, message } from 'antd';
import useCustomerDetail from './hooks/useCustomerDetail';
import SubscriptionTable from './components/customerSubscription';
import InvoiceTable from './components/customerInvoice';
import TeamTable from './components/teamList';
import TeamMemberModal from './components/teamMemberModal';
import useInvoiceState from '../invoice/hooks/useInvoiceState';

const { Panel } = Collapse;

const CustomerDetailPage = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { id } = useParams();
  const { 
    customer, 
    subscriptions, 
    invoices,
    loading, 
    addOrUpdateTeamMember, 
    markTeamMemberInactive
  } = useCustomerDetail(id);
  const { downloadPDFFile } = useInvoiceState();

  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [editingCustomerTeamMember, setEditingCustomerTeamMember] = useState(null);

  const handleTeamMemberAdd = () => {
    setEditingCustomerTeamMember(null);
    setTeamModalOpen(true);
  };

  const handleTeamMemberEdit = (customerTeamMember) => {
    setEditingCustomerTeamMember(customerTeamMember);
    setTeamModalOpen(true);
  };

  const downloadPDF = async (invoice_id) => {
    const res = await downloadPDFFile({ invoice_id });
    // console.log({ pdfResponse: res });
    if (res.success) {
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.fileName;
      a.click();
      messageApi.success('Invoice downloaded', 5);
    } else {
      messageApi.error(res.message, 5);
    }
  }

  const handleTeamMemberSave = async (customerTeamMember) => {
    const response = await addOrUpdateTeamMember(customerTeamMember);
    if (response?.success) {
      setTeamModalOpen(false);
      let toastMessage = 'Team Member created successfully';
      if (editingCustomerTeamMember && editingCustomerTeamMember.id) {
        toastMessage = ' Team Member updated successfully';
      }
      messageApi.success(toastMessage, 5);
    }
    return response;
  };

  const handleTeamMemberDelete = async (teamMember_id, date, id) => {
    await markTeamMemberInactive(teamMember_id, date, id);
  };

  if (loading) return <Spin tip="Loading..." />;

  if (!customer) return <p>Customer not found.</p>;

  const customerName = (name) => {
    const nameSplit = String(name).split('\n').map((line, idx) => {
      return <div key={idx}>{line}</div>
    });
    return (<>
      {nameSplit}
    </>)
  }

  return (
    <Card title={customer.name}>
      {contextHolder}
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Code">{customer.code}</Descriptions.Item>
        <Descriptions.Item label="Customer">{customerName(customer.name)}</Descriptions.Item>
        <Descriptions.Item label="contact Person">{customer.contact_person}</Descriptions.Item>
        <Descriptions.Item label="Email">{customer.email}</Descriptions.Item>
        <Descriptions.Item label="Address">{customer.address}, {customer.city}, {customer.state}, {customer.zip}, {customer.country}</Descriptions.Item>
      </Descriptions>

      <Collapse className='mt-24' defaultActiveKey={['1']}>
        <Panel header="Subscriptions" key="1">
          <SubscriptionTable data={subscriptions} />
        </Panel>
        <Panel header="Team" key="2">
          <>
            <TeamTable
              data={customer.team} 
              loading={loading}
              onEdit={handleTeamMemberEdit}
              markInactive={handleTeamMemberDelete}
              onAdd={handleTeamMemberAdd}
            />
            <TeamMemberModal
              visible={teamModalOpen}
              onCancel={() => setTeamModalOpen(false)}
              onSave={handleTeamMemberSave}
              initialData={editingCustomerTeamMember}
              customer={customer}
            />
          </>
        </Panel>
        <Panel header="Invoices" key="3">
          <InvoiceTable invoices={invoices} loading={loading} downloadPDF={downloadPDF}/>
        </Panel>
      </Collapse>
    </Card>
  );
};

export default CustomerDetailPage;
